import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getEmployees, deleteEmployee, getCantons, getSalaryCertificate } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Calendar,
  Briefcase,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  UserCircle,
  Building,
  ChevronRight,
  Award,
  Download,
} from 'lucide-react';
import { createEmployee, updateEmployee } from '../lib/api';

const EmployeesPage = () => {
  const { t } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewEmployee, setViewEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    civil_status: 'single',
    children_count: 0,
    nationality: 'Suisse',
    address: '',
    postal_code: '',
    city: '',
    canton: 'GE',
    country: 'Suisse',
    permit_type: 'swiss',
    iban: '',
    bank_name: '',
    contract_type: 'cdi',
    start_date: '',
    position: '',
    department: '',
    work_rate: 100,
    gross_monthly_salary: '',
    has_13th_month: true,
    thirteenth_month_type: 'monthly',
    avs_number: '',
    lpp_enrolled: true,
    meal_allowance: 0,
    transport_allowance: 0,
    other_allowances: 0,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, cantonsRes] = await Promise.all([
        getEmployees({ active_only: false }),
        getCantons()
      ]);
      setEmployees(empRes.data || []);
      setCantons(cantonsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('hr.loadError') || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      civil_status: 'single',
      children_count: 0,
      nationality: 'Suisse',
      address: '',
      postal_code: '',
      city: '',
      canton: 'GE',
      country: 'Suisse',
      permit_type: 'swiss',
      iban: '',
      bank_name: '',
      contract_type: 'cdi',
      start_date: '',
      position: '',
      department: '',
      work_rate: 100,
      gross_monthly_salary: '',
      has_13th_month: true,
      thirteenth_month_type: 'monthly',
      avs_number: '',
      lpp_enrolled: true,
      meal_allowance: 0,
      transport_allowance: 0,
      other_allowances: 0,
    });
    setEditingEmployee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        gross_monthly_salary: parseFloat(formData.gross_monthly_salary) || 0,
        work_rate: parseFloat(formData.work_rate) || 100,
        children_count: parseInt(formData.children_count) || 0,
        meal_allowance: parseFloat(formData.meal_allowance) || 0,
        transport_allowance: parseFloat(formData.transport_allowance) || 0,
        other_allowances: parseFloat(formData.other_allowances) || 0,
      };

      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, payload);
        toast.success(t('hr.employeeUpdated') || 'Employé mis à jour');
      } else {
        await createEmployee(payload);
        toast.success(t('hr.employeeCreated') || 'Employé créé');
      }
      
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error(error.response?.data?.detail || t('hr.saveError') || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      date_of_birth: employee.date_of_birth?.split('T')[0] || '',
      civil_status: employee.civil_status || 'single',
      children_count: employee.children_count || 0,
      nationality: employee.nationality || 'Suisse',
      address: employee.address || '',
      postal_code: employee.postal_code || '',
      city: employee.city || '',
      canton: employee.canton || 'GE',
      country: employee.country || 'Suisse',
      permit_type: employee.permit_type || 'swiss',
      iban: employee.iban || '',
      bank_name: employee.bank_name || '',
      contract_type: employee.contract_type || 'cdi',
      start_date: employee.start_date?.split('T')[0] || '',
      position: employee.position || '',
      department: employee.department || '',
      work_rate: employee.work_rate || 100,
      gross_monthly_salary: employee.gross_monthly_salary || '',
      has_13th_month: employee.has_13th_month ?? true,
      thirteenth_month_type: employee.thirteenth_month_type || 'monthly',
      avs_number: employee.avs_number || '',
      lpp_enrolled: employee.lpp_enrolled ?? true,
      meal_allowance: employee.meal_allowance || 0,
      transport_allowance: employee.transport_allowance || 0,
      other_allowances: employee.other_allowances || 0,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('hr.confirmDelete') || 'Supprimer cet employé ?')) return;
    
    try {
      await deleteEmployee(id);
      toast.success(t('hr.employeeDeleted') || 'Employé supprimé');
      loadData();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error(t('hr.deleteError') || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadCertificate = async (employeeId, employeeName, year) => {
    try {
      const response = await getSalaryCertificate(employeeId, year);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificat_salaire_${employeeName.replace(' ', '_')}_${year}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('hr.certificateDownloaded') || 'Certificat téléchargé');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error(error.response?.data?.detail || t('hr.certificateError') || 'Erreur: Aucune fiche de paie validée trouvée');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           emp.position?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getContractBadge = (type) => {
    const colors = {
      cdi: 'bg-green-100 text-green-800',
      cdd: 'bg-blue-100 text-blue-800',
      temporary: 'bg-orange-100 text-orange-800',
      apprentice: 'bg-purple-100 text-purple-800',
      intern: 'bg-gray-100 text-gray-800',
    };
    const labels = {
      cdi: 'CDI',
      cdd: 'CDD',
      temporary: 'Intérim',
      apprentice: 'Apprenti',
      intern: 'Stagiaire',
    };
    return <Badge className={colors[type]}>{labels[type] || type}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { 
      style: 'currency', 
      currency: 'CHF',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            {t('hr.employees') || 'Employés'}
          </h1>
          <p className="text-slate-500 mt-1">
            {employees.length} {t('hr.employeesCount') || 'employé(s)'}
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('hr.addEmployee') || 'Ajouter un employé'}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={t('hr.searchEmployees') || 'Rechercher un employé...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-employees"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          {filteredEmployees.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {t('hr.noEmployees') || 'Aucun employé'}
              </h3>
              <p className="text-slate-500 mb-4">
                {t('hr.addFirstEmployee') || 'Ajoutez votre premier employé'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('hr.name') || 'Nom'}</TableHead>
                  <TableHead>{t('hr.position') || 'Poste'}</TableHead>
                  <TableHead>{t('hr.contract') || 'Contrat'}</TableHead>
                  <TableHead className="text-right">{t('hr.salary') || 'Salaire'}</TableHead>
                  <TableHead>{t('hr.workRate') || 'Taux'}</TableHead>
                  <TableHead className="text-right">{t('common.actions') || 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow 
                    key={employee.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setViewEmployee(employee)}
                    data-testid={`employee-row-${employee.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{employee.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-slate-900">{employee.position}</p>
                        {employee.department && (
                          <p className="text-sm text-slate-500">{employee.department}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getContractBadge(employee.contract_type)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(employee.gross_monthly_salary)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{employee.work_rate}%</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(employee)}
                          data-testid={`edit-employee-${employee.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(employee.id)}
                          data-testid={`delete-employee-${employee.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Employee Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-blue-600" />
              {editingEmployee 
                ? (t('hr.editEmployee') || 'Modifier l\'employé')
                : (t('hr.newEmployee') || 'Nouvel employé')
              }
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 border-b pb-2">
                <UserCircle className="w-4 h-4" />
                {t('hr.personalInfo') || 'Informations personnelles'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('hr.firstName') || 'Prénom'} *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label>{t('hr.lastName') || 'Nom'} *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    required
                    data-testid="input-last-name"
                  />
                </div>
                <div>
                  <Label>{t('hr.dateOfBirth') || 'Date de naissance'} *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    required
                    data-testid="input-dob"
                  />
                </div>
                <div>
                  <Label>{t('hr.email') || 'Email'}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label>{t('hr.phone') || 'Téléphone'}</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label>{t('hr.civilStatus') || 'État civil'}</Label>
                  <Select value={formData.civil_status} onValueChange={(v) => handleInputChange('civil_status', v)}>
                    <SelectTrigger data-testid="select-civil-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t('hr.single') || 'Célibataire'}</SelectItem>
                      <SelectItem value="married">{t('hr.married') || 'Marié(e)'}</SelectItem>
                      <SelectItem value="divorced">{t('hr.divorced') || 'Divorcé(e)'}</SelectItem>
                      <SelectItem value="widowed">{t('hr.widowed') || 'Veuf/Veuve'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('hr.children') || 'Nombre d\'enfants'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children_count}
                    onChange={(e) => handleInputChange('children_count', e.target.value)}
                    data-testid="input-children"
                  />
                </div>
                <div>
                  <Label>{t('hr.nationality') || 'Nationalité'}</Label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    data-testid="input-nationality"
                  />
                </div>
                <div>
                  <Label>{t('hr.permitType') || 'Type de permis'}</Label>
                  <Select value={formData.permit_type} onValueChange={(v) => handleInputChange('permit_type', v)}>
                    <SelectTrigger data-testid="select-permit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="swiss">Suisse</SelectItem>
                      <SelectItem value="permit_c">Permis C (Établissement)</SelectItem>
                      <SelectItem value="permit_b">Permis B (Séjour)</SelectItem>
                      <SelectItem value="permit_g">Permis G (Frontalier)</SelectItem>
                      <SelectItem value="permit_l">Permis L (Court séjour)</SelectItem>
                      <SelectItem value="eu_efta">UE/AELE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 border-b pb-2">
                <MapPin className="w-4 h-4" />
                {t('hr.address') || 'Adresse'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <Label>{t('hr.street') || 'Rue'} *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <Label>{t('hr.postalCode') || 'NPA'} *</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    required
                    data-testid="input-postal"
                  />
                </div>
                <div>
                  <Label>{t('hr.city') || 'Ville'} *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label>{t('hr.canton') || 'Canton'} *</Label>
                  <Select value={formData.canton} onValueChange={(v) => handleInputChange('canton', v)}>
                    <SelectTrigger data-testid="select-canton">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cantons.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name_fr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contract & Employment */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 border-b pb-2">
                <Briefcase className="w-4 h-4" />
                {t('hr.employment') || 'Emploi'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('hr.position') || 'Poste'} *</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    required
                    data-testid="input-position"
                  />
                </div>
                <div>
                  <Label>{t('hr.department') || 'Département'}</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    data-testid="input-department"
                  />
                </div>
                <div>
                  <Label>{t('hr.contractType') || 'Type de contrat'}</Label>
                  <Select value={formData.contract_type} onValueChange={(v) => handleInputChange('contract_type', v)}>
                    <SelectTrigger data-testid="select-contract">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cdi">CDI</SelectItem>
                      <SelectItem value="cdd">CDD</SelectItem>
                      <SelectItem value="temporary">Intérim</SelectItem>
                      <SelectItem value="apprentice">Apprentissage</SelectItem>
                      <SelectItem value="intern">Stage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('hr.startDate') || 'Date d\'entrée'} *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    required
                    data-testid="input-start-date"
                  />
                </div>
                <div>
                  <Label>{t('hr.workRate') || 'Taux d\'activité (%)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.work_rate}
                    onChange={(e) => handleInputChange('work_rate', e.target.value)}
                    data-testid="input-work-rate"
                  />
                </div>
                <div>
                  <Label>{t('hr.avsNumber') || 'N° AVS'}</Label>
                  <Input
                    placeholder="756.XXXX.XXXX.XX"
                    value={formData.avs_number}
                    onChange={(e) => handleInputChange('avs_number', e.target.value)}
                    data-testid="input-avs"
                  />
                </div>
              </div>
            </div>

            {/* Salary */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 border-b pb-2">
                <CreditCard className="w-4 h-4" />
                {t('hr.salaryInfo') || 'Salaire'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('hr.grossSalary') || 'Salaire brut mensuel (CHF)'} *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    value={formData.gross_monthly_salary}
                    onChange={(e) => handleInputChange('gross_monthly_salary', e.target.value)}
                    required
                    data-testid="input-salary"
                  />
                </div>
                <div>
                  <Label>{t('hr.has13thMonth') || '13ème salaire'}</Label>
                  <Select 
                    value={formData.has_13th_month ? 'yes' : 'no'} 
                    onValueChange={(v) => handleInputChange('has_13th_month', v === 'yes')}
                  >
                    <SelectTrigger data-testid="select-13th">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">{t('common.yes') || 'Oui'}</SelectItem>
                      <SelectItem value="no">{t('common.no') || 'Non'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('hr.lppEnrolled') || 'Affilié LPP'}</Label>
                  <Select 
                    value={formData.lpp_enrolled ? 'yes' : 'no'} 
                    onValueChange={(v) => handleInputChange('lpp_enrolled', v === 'yes')}
                  >
                    <SelectTrigger data-testid="select-lpp">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">{t('common.yes') || 'Oui'}</SelectItem>
                      <SelectItem value="no">{t('common.no') || 'Non'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('hr.iban') || 'IBAN'} *</Label>
                  <Input
                    placeholder="CH93 0000 0000 0000 0000 0"
                    value={formData.iban}
                    onChange={(e) => handleInputChange('iban', e.target.value)}
                    required
                    data-testid="input-iban"
                  />
                </div>
                <div>
                  <Label>{t('hr.bankName') || 'Nom de la banque'}</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    data-testid="input-bank"
                  />
                </div>
              </div>
              
              {/* Allowances */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div>
                  <Label>{t('hr.mealAllowance') || 'Indemnité repas (CHF)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.meal_allowance}
                    onChange={(e) => handleInputChange('meal_allowance', e.target.value)}
                    data-testid="input-meal"
                  />
                </div>
                <div>
                  <Label>{t('hr.transportAllowance') || 'Indemnité transport (CHF)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.transport_allowance}
                    onChange={(e) => handleInputChange('transport_allowance', e.target.value)}
                    data-testid="input-transport"
                  />
                </div>
                <div>
                  <Label>{t('hr.otherAllowances') || 'Autres indemnités (CHF)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.other_allowances}
                    onChange={(e) => handleInputChange('other_allowances', e.target.value)}
                    data-testid="input-other"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                {t('common.cancel') || 'Annuler'}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-employee-btn">
                {editingEmployee 
                  ? (t('common.save') || 'Enregistrer')
                  : (t('hr.addEmployee') || 'Ajouter')
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Dialog */}
      <Dialog open={!!viewEmployee} onOpenChange={() => setViewEmployee(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-bold text-lg">
                  {viewEmployee?.first_name?.charAt(0)}{viewEmployee?.last_name?.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-xl">{viewEmployee?.first_name} {viewEmployee?.last_name}</p>
                <p className="text-sm font-normal text-slate-500">{viewEmployee?.position}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {viewEmployee && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{viewEmployee.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{viewEmployee.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{viewEmployee.address}, {viewEmployee.postal_code} {viewEmployee.city}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span>{viewEmployee.department || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>Depuis le {viewEmployee.start_date?.split('T')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{formatCurrency(viewEmployee.gross_monthly_salary)}/mois</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => {
                  setViewEmployee(null);
                  handleEdit(viewEmployee);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  {t('common.edit') || 'Modifier'}
                </Button>
                <Button variant="outline" className="flex-1">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('hr.viewPayslips') || 'Fiches de paie'}
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => handleDownloadCertificate(
                    viewEmployee.id, 
                    `${viewEmployee.first_name} ${viewEmployee.last_name}`,
                    new Date().getFullYear()
                  )}
                  data-testid="download-certificate-btn"
                >
                  <Award className="w-4 h-4 mr-2" />
                  {t('hr.salaryCertificate') || 'Certificat ' + new Date().getFullYear()}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
